import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

interface ICoachMarkRegistration {
  id: string;
  order: number;
  // Mounted instances of this id in registration order; only the first one shows.
  instanceIds: string[];
}

interface ICoachMarkInstance {
  id: string;
  instanceId: string;
}

interface ICoachMarkSliceState {
  registered: Record<string, ICoachMarkRegistration>;
  seen: Record<string, true>;
}

const STORAGE_KEY = 'coachMarkSeen';

const loadSeen = (): Record<string, true> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return {};
    return Object.fromEntries(parsed.filter((id): id is string => typeof id === 'string').map(id => [id, true]));
  } catch {
    return {};
  }
};

const persistSeen = (seen: Record<string, true>): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.keys(seen)));
  } catch {
    // localStorage may throw on quota or in private-browsing modes — best-effort persistence.
  }
};

const initialState: ICoachMarkSliceState = {
  registered: {},
  seen: loadSeen(),
};

const coachMarkSlice = createSlice({
  name: 'coachMarkSlice',
  initialState,
  reducers: {
    register(state, action: PayloadAction<ICoachMarkInstance & { order: number }>) {
      const { id, order, instanceId } = action.payload;
      // Once seen, a coach mark is permanently retired — block re-registration
      // so the position counter doesn't pick up ghosts from past sessions.
      if (state.seen[id]) {
        return;
      }
      const registration = state.registered[id];
      if (registration) {
        registration.instanceIds.push(instanceId);
      } else {
        state.registered[id] = { id, order, instanceIds: [instanceId] };
      }
    },
    unregister(state, action: PayloadAction<ICoachMarkInstance>) {
      const { id, instanceId } = action.payload;
      const registration = state.registered[id];
      if (!registration) return;
      registration.instanceIds = registration.instanceIds.filter(other => other !== instanceId);
      if (registration.instanceIds.length === 0) {
        delete state.registered[id];
      }
    },
    markSeen(state, action: PayloadAction<string>) {
      state.seen[action.payload] = true;
      // If nothing unseen remains, the tour is done — drop the whole registered
      // set so a later remount can't resurrect them into a future queue.
      const hasUnseen = Object.values(state.registered).some(r => !state.seen[r.id]);
      if (!hasUnseen) {
        state.registered = {};
      }
      persistSeen(state.seen);
    },
    skipAll(state) {
      for (const id of Object.keys(state.registered)) {
        state.seen[id] = true;
      }
      state.registered = {};
      persistSeen(state.seen);
    },
    // Remove specific ids from the seen set. Lets a host (e.g. a demo page or a
    // "Show me again" admin control) un-retire coach marks so they can re-show.
    // Doesn't re-register — callers should remount the relevant <CoachMark>s.
    forget(state, action: PayloadAction<string[]>) {
      for (const id of action.payload) {
        delete state.seen[id];
      }
      persistSeen(state.seen);
    },
  },
});

export default coachMarkSlice.reducer;
export const coachMarkActions = coachMarkSlice.actions;

export const selectCoachMarkRegistered = (state: RootState) => state.coachMarkSlice.registered;
export const selectCoachMarkSeen = (state: RootState) => state.coachMarkSlice.seen;

export const computeFullQueue = (registered: Record<string, ICoachMarkRegistration>): string[] =>
  Object.values(registered)
    .sort((a, b) => a.order - b.order)
    .map(r => r.id);
